import { useAuth0 } from '@auth0/auth0-react';
import { usePersistentRequestKey } from '@ths/shared/hooks';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import {
  HotelBookingAttemptsQuery,
  useCloseUnreceivedHotelBookingRequestMutation,
  useHotelBookingAttemptsQuery,
} from '../generated/graphql';

import { useHotelScope } from './scope';

type Attempt = HotelBookingAttemptsQuery['hotelBookingAttempts'][number];
type Tracking = {
  attempt: Attempt | null;
  bookingIntentId: string | null;
  requestKey: string | null;
  pending: boolean;
  ready: boolean;
  checking: boolean;
  closing: boolean;
  notice: string | null;
  statusUnavailable: boolean;
  canCloseUnreceived: boolean;
  trackIntent: (bookingIntentId: string | null) => void;
  begin: () => Promise<string>;
  refresh: () => Promise<void>;
  closeUnreceived: () => Promise<boolean>;
  observeError: (error: unknown, submittedKey: string) => Promise<void>;
  clearResolved: (requestKey?: string) => Promise<boolean>;
  confirmed: (submittedKey: string) => Promise<boolean>;
  isPricingConsumed: (id: string) => boolean;
};
const Context = createContext<Tracking | null>(null);
export function useHotelBookingTracking() {
  const value = useContext(Context);
  if (!value) throw new Error('Booking status is unavailable.');
  return value;
}

export function HotelBookingAttemptsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useAuth0();
  const scope = useHotelScope();
  const [bookingIntentId, setBookingIntentId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [consumed, setConsumed] = useState<string[]>([]);
  const keys = usePersistentRequestKey(
    user?.sub && scope && bookingIntentId
      ? `v2:hotel:CLIENT:${user.sub}:${scope.clientId}:${bookingIntentId}`
      : null,
  );
  const rememberRequest = keys.remember;
  const active = useHotelBookingAttemptsQuery({
    variables: { bookingIntentId },
    skip: !bookingIntentId,
    fetchPolicy: 'network-only',
  });
  const known = useHotelBookingAttemptsQuery({
    variables: { requestKey: keys.requestKey },
    skip: !keys.requestKey,
    fetchPolicy: 'network-only',
  });
  const knownAttempt = keys.requestKey
    ? known.data?.hotelBookingAttempts.find(
        (item) =>
          item.requestKey === keys.requestKey &&
          item.bookingIntentId === bookingIntentId,
      )
    : null;
  const activeAttempts =
    active.data?.hotelBookingAttempts.filter(
      (item) =>
        item.bookingIntentId === bookingIntentId &&
        !dismissed.includes(item.requestKey),
    ) ?? [];
  // A different historical attempt cannot establish the held request's outcome.
  const attempt = keys.requestKey
    ? (knownAttempt ??
      activeAttempts.find((item) => item.requestKey === keys.requestKey) ??
      null)
    : (activeAttempts[0] ?? null);
  const [closeRequest, { loading: closing }] =
    useCloseUnreceivedHotelBookingRequestMutation();
  const [notice, setNotice] = useState<string | null>(null);
  const pending =
    Boolean(keys.requestKey || attempt) &&
    (!attempt ||
      ['PROCESSING', 'RECONCILIATION_REQUIRED'].includes(attempt.status));
  const ready =
    Boolean(bookingIntentId) &&
    !active.loading &&
    !active.error &&
    (!keys.requestKey || (!known.loading && !known.error));
  const { startPolling: startActive, stopPolling: stopActive } = active;
  const { startPolling: startKnown, stopPolling: stopKnown } = known;
  useEffect(() => {
    if (bookingIntentId && pending) startActive(5000);
    if (keys.requestKey && pending) startKnown(5000);
    return () => {
      stopActive();
      stopKnown();
    };
  }, [
    bookingIntentId,
    keys.requestKey,
    pending,
    startActive,
    startKnown,
    stopActive,
    stopKnown,
  ]);

  useEffect(() => {
    if (attempt)
      setConsumed((previous) =>
        previous.includes(attempt.pricingId)
          ? previous
          : [...previous, attempt.pricingId],
      );
    if (
      attempt &&
      !keys.requestKey &&
      !dismissed.includes(attempt.requestKey) &&
      ['PROCESSING', 'RECONCILIATION_REQUIRED'].includes(attempt.status)
    )
      void rememberRequest(attempt.requestKey).catch(() =>
        setNotice('Booking status could not be retained in this browser.'),
      );
  }, [attempt, dismissed, keys.requestKey, rememberRequest]);
  const trackIntent = useCallback((intentId: string | null) => {
    setBookingIntentId(intentId);
    setNotice(null);
  }, []);
  const refresh = async (requestKey = keys.requestKey) => {
    const results = await Promise.allSettled([
      active.refetch(),
      ...(requestKey ? [known.refetch({ requestKey })] : []),
    ]);
    if (results.some((result) => result.status === 'rejected'))
      setNotice(
        'Booking status could not be checked. Keep this request and try checking again.',
      );
    else setNotice(null);
  };
  const closeUnreceived = async () => {
    const requestKey = keys.requestKey;
    if (!requestKey) return false;
    try {
      const result = await closeRequest({ variables: { requestKey } });
      const check = result.data?.closeUnreceivedHotelBookingRequest;
      if (check?.closed && !check.attempt) {
        const cleared = await keys.replace(requestKey, null);
        setNotice(null);
        await refresh();
        return (
          cleared &&
          !activeAttempts.some((item) =>
            ['PROCESSING', 'RECONCILIATION_REQUIRED'].includes(item.status),
          )
        );
      } else if (check?.attempt) {
        await keys.replace(requestKey, check.attempt.requestKey);
      }
      await refresh();
    } catch {
      setNotice(
        'We could not check this booking. Please check again before trying to book.',
      );
    }
    return false;
  };
  const value: Tracking = {
    attempt,
    bookingIntentId,
    requestKey: keys.requestKey,
    pending,
    ready,
    checking: active.loading || known.loading,
    closing,
    notice,
    statusUnavailable: Boolean(
      active.error || (keys.requestKey && known.error) || notice,
    ),
    canCloseUnreceived:
      !knownAttempt &&
      Boolean(keys.requestKey) &&
      attempt?.requestKey !== keys.requestKey &&
      ready,
    trackIntent,
    isPricingConsumed: (id) => consumed.includes(id),
    begin: async () => {
      if (!ready || pending || keys.requestKey)
        throw new Error('Check this booking status before submitting again.');
      const key = await keys.begin();
      return key;
    },
    refresh,
    closeUnreceived,
    observeError: async (error, submittedKey) => {
      const errors = (
        error as { graphQLErrors?: { extensions?: { requestKey?: string } }[] }
      )?.graphQLErrors;
      const key = errors?.find((item) => item.extensions?.requestKey)
        ?.extensions?.requestKey;
      if (key && key !== submittedKey) await keys.replace(submittedKey, key);
      // The mutation may have started before React observed the newly stored key.
      // Always refresh that exact request as well as the current intent.
      await refresh(key ?? submittedKey);
    },
    clearResolved: async (requestKey) => {
      const resolved = knownAttempt ?? attempt;
      if (
        resolved &&
        (!requestKey || resolved.requestKey === requestKey) &&
        !['PROCESSING', 'RECONCILIATION_REQUIRED'].includes(resolved.status)
      ) {
        if (keys.requestKey && keys.requestKey !== resolved.requestKey)
          return false;
        const cleared = await keys.replace(keys.requestKey, null);
        if (!cleared) return false;
        setConsumed((previous) => [...previous, resolved.pricingId]);
        setDismissed((previous) => [...previous, resolved.requestKey]);
        return true;
      }
      return !keys.requestKey && !pending;
    },
    confirmed: async (submittedKey) => {
      // A failed write must leave the known result available for another check.
      // A missing key is fine, but a different/newer key must survive unchanged.
      const cleared = await keys.replace(submittedKey, null);
      if (!cleared && !(await keys.replace(null, null))) return false;
      setDismissed((previous) => [...previous, submittedKey]);
      void active.refetch();
      return true;
    },
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
}
